<template>
  <v-container fluid>
    <v-row align="center" justify="center">
      <v-col class="d-flex justify-center align-center" md="8">
        <p class="mr-4 mb-5">{{ $t("filters.prepend") }}</p>
        <v-select :items="periods" :label="$t('period')" :placeholder="$t('filters.currentMonth')" v-model="periodSelected" outlined @change="periodChanged"></v-select>
        <p class="ml-4 mr-4 mb-5">{{ $t("filters.and") }}</p>
        <v-select 
          ref="Accounts"
          multiple
          height="36"
          :items="[allAccountsOption, ...brokerAccountItems]"
          :label="$t('brokerAccounts')" 
          :placeholder="$t('allBrokers')"
          v-model="accountSelected" 
          outlined 
          @change="brokerChanged"
          class="broker-select"
          return-object
        >
          <template v-slot:selection="{ item, index }">
            <template v-if="isAllSelected">
              {{ $t("allAccounts") }}
            </template>
            <template v-else>
              <span v-if="index === 0" class="account-selection-text">
                {{ item.text }}
                <span v-if="accountSelected.length > 1">
                  +{{ accountSelected.length - 1 }}
                </span>
              </span>
            </template>
          </template>
          <template v-slot:item="{ item }">
            <div class="d-flex align-center">
              {{ item.text }}
              <img v-if="item.verified" 
                src="@/assets/icons/Audited.svg" 
                alt="Audited Icon" 
                class="ml-2" 
                style="width: 14px; height: 14px;"
              />
            </div>
          </template>
        </v-select>
        <p class="ml-4 mb-5">
          {{  accountSelected.length > 1 || isAllSelected ? $t('filters.accounts') : $t('filters.account') }}
        </p>
      </v-col>
    </v-row>

    <v-row class="mt-n3" align="center" justify="center">
      <v-col v-bind:key="ratio" v-for="ratio in ratios">
        <v-card :class="getRatioBorder(ratio) + ' ratiocard'" outlined>
          <v-card-text>
            <h2 class="text-center">{{ getRatio(ratio) }}</h2>
            <p class="text-center">{{ getRatioName(ratio) }}</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-divider class="mt-6 mb-8" />

    <v-row style="position: relative">
      <div style="width: 100%">
        <h2 class="text-center my-5">
          {{ $t("insights.title") }} {{ accountBasicName }}
          <img v-if="accountVerified" src="@/assets/icons/Audited.svg" alt="Audited Icon" class="ml-1 mt-n1"
            style="width: 16px; height: 16px; color: blue" />
        </h2>
        <InsightsSummary v-blur="blurConfig" :insights="priorityInsights" :full="false" />
        <v-overlay :dark="false" v-model="canSeeStat" absolute opacity="0">
          <v-btn class="button-convert" @click="overlayUpgradeToPro = true">{{ $t("btnCTA") }}
            <v-icon class="ml-2" small>fa-star</v-icon>
          </v-btn>
        </v-overlay>
      </div>
    </v-row>

    <v-overlay :dark="false" v-model="overlayUpgradeToPro">
      <UpgradeToPro :message="messageUpgradeToPro" @closeWindow="closeOverlayUpgradeToPro" />
    </v-overlay>
  </v-container>
</template>

<script>
import Repository from "../../../repositories/journal";
import InsightsRepository from "../../../repositories/insights";
import InsightsData from "../../Mentor/insightsData";
import InsightsSummary from "@/views/Mentor/components/InsightsSummary";
import UpgradeToPro from "@/components/UpgradeToPro";
import Moods from "../../Journal/moods";

export default {
  name: "AnalyticsSummary",
  components: { UpgradeToPro, InsightsSummary },
  data() {
    return {
      // Cards
      analytics: null,
      periods: [
        { value: "Current week", text: this.$t("filters.currentWeek") },
        { value: "Current month", text: this.$t("filters.currentMonth") },
        { value: "Last 30 days", text: this.$t("filters.last30days") },
        { value: "Last 90 days", text: this.$t("filters.last90days") },
      ],
      periodSelected: "Current month",
      brokerAccounts: [],
      accountSelected: [], // Remove initial value, will be set in init()
      ratios: [
        "Profit Factor",
        "Winning Ratio",
        "Swiset Ratio",
        "Risk/Reward Ratio",
        "Net Profit",
      ],

      // Insights
      allInsights: [],
      priorityInsights: {},
      blurConfig: {
        isBlurred: !this.$store.state.user.isPro,
        opacity: 0.1,
        filter: "blur(1.2px)",
        transition: "all .3s linear",
      },
      canSeeStat: !this.$store.state.user.isPro,
      messageUpgradeToPro: "With pro you can get unlimited trading insights.",

      // Upgrade to pro
      overlayUpgradeToPro: false,
    };
  },
  computed: {
    accountBasicName() {
      if (!this.accountSelected || !this.accountSelected.length) return this.$t("allAccounts");
      
      if (this.accountSelected.some(item => item.isAll)) {
        return this.$t("allAccounts");
      }

      if (this.accountSelected.length === 1) {
        const selectedId = Number(this.accountSelected[0].value);
        const accountFound = this.$store.state.user.brokerAccounts.find(
          (account) => account.id === selectedId
        );
        if (!accountFound) return '';
        return accountFound.identifier 
          ? `${accountFound.broker.name} · ${accountFound.identifier}`
          : accountFound.broker.name;
      }
      return this.$t("multipleAcc");
    },
    accountVerified() {
      if (!this.accountSelected || this.accountSelected.length !== 1) return false;
      
      const selectedId = Number(this.accountSelected[0].value);
      const accountFound = this.$store.state.user.brokerAccounts.find(
        (account) => account.id === selectedId
      );
      return accountFound?.verified || false;
    },
    allAccountsOption() {
      return {
        text: this.$t("allAccounts"),
        value: 'all',
        isAll: true
      };
    },
    brokerAccountItems() {
      return this.brokerAccounts.map((acc) => ({
        value: Number(acc.id),
        text: `${acc.identifier} (${acc.broker.name})`,
        isAll: false,
        verified: acc.verified,
      }));
    },
    hasAllSelected() {
      return this.accountSelected.some(item => item.isAll);
    },
    isAllSelected() {
      return this.accountSelected.some(item => item.isAll);
    },
    allAccountIds() {
      return this.brokerAccounts.map(acc => Number(acc.id));
    }
  },
  watch: {
    "$store.state.user.brokerAccounts": async function () {
      await this.init();
      this.updateVerifiedAcc();
    },
    "$store.state.user.isPro": function () {
      this.blurConfig = {
        isBlurred: !this.$store.state.user.isPro,
        opacity: 0.1,
        filter: "blur(1.2px)",
        transition: "all .3s linear",
      };
      this.canSeeStat = !this.$store.state.user.isPro;
    },
  },
  created() {
    this.init();
  },
  mounted() {
    this.updateVerifiedAcc();
  },
  methods: {
    init() {
      this.brokerAccounts = [...this.$store.state.user.brokerAccounts];
      
      // Initialize with "All" option
      this.accountSelected = [{
        value: 'all',
        text: this.$t("allAccounts"),
        isAll: true
      }];

      // Dates
      let nowFilters = new Date();
      nowFilters.setDate(nowFilters.getDate() + 1);
      let fromFilters = new Date();
      fromFilters = new Date(
        fromFilters.getFullYear(),
        fromFilters.getMonth(),
        1
      );

      if (this.$store.state.user.assets.map((asset) => asset.ticker).length) {
        let filter = {
          allStrategies: true,
          userId: this.$store.state.user.id,
          fromDate: new Date(
            fromFilters.getFullYear(),
            fromFilters.getMonth(),
            fromFilters.getDate(),
            0,
            0
          ),
          toDate: new Date(
            nowFilters.getFullYear(),
            nowFilters.getMonth(),
            nowFilters.getDate(),
            0,
            0
          ),
          side: "All",
          moods: Moods.map((m) => m.key),
          assets: this.$store.state.user.assets.map((asset) => asset.ticker),
          brokerAccounts: this.$store.state.user.brokerAccounts.map((acc) =>
            Number(acc.id)
          ),
          result: "All",
          strategies: ["-1"],
        };

        Repository.analyticsSummary(filter).then((response) => {
          if (response.status < 400) {
            this.analytics = response.data.content[0];
          }
        });
        InsightsRepository.getUserInsights().then((response) => {
          this.allInsights = [];
          if (response.status < 400) {
            response.data.forEach((insight) => {
              this.allInsights.push(JSON.parse(insight));
            });
          }
          this.setPriorityInsights();
        });
      }
    },
    updateVerifiedAcc() {
      // Only run if refs.Accounts exists
      if (!this.$refs.Accounts?.$el) return;

      const store = this.$store;
      const accSelect = this.$refs.Accounts.$el;
      const options = accSelect.querySelectorAll(".option");

      options.forEach((el, index) => {
        if (index > 1) {
          const identifier = el.textContent.split("~")[0];
          const account = store.state.user.brokerAccounts.find(
            (b) => b.identifier?.trim() === identifier?.trim()
          );
          
          if (account?.verified) {
            const icon = document.createElement("img");
            icon.src = require("@/assets/icons/Audited.svg");
            icon.alt = "Audited Icon";
            icon.style.position = "absolute";
            icon.style.width = "16px";
            icon.style.height = "16px";
            icon.style.right = "0";
            el.appendChild(icon);
          }
        }
      });
    },
    setPriorityInsights() {
      if (!this.allInsights?.length) return;

      let partialInsights;
      if (this.accountSelected.some(item => item.isAll)) {
        partialInsights = this.allInsights.find(insightsData => insightsData.id === -1);
      } else if (this.accountSelected.length === 1) {
        const selectedId = Number(this.accountSelected[0].value);
        partialInsights = this.allInsights.find(
          insightsData => insightsData.id === selectedId
        );
      }

      if (!partialInsights?.brokerAccountInsights?.length) return;

      //let partialInsights = this.allInsights[this.allInsights.length - 1];
      for (let i = 0; i < partialInsights.brokerAccountInsights.length; i++) {
        let foundInsight = InsightsData.find(
          (insightMaster) =>
            insightMaster.insightType ===
            partialInsights.brokerAccountInsights[i].insightType
        );
        if (foundInsight !== undefined)
          partialInsights.brokerAccountInsights[i].priority =
            foundInsight.priority;
      }
      partialInsights.brokerAccountInsights.sort(
        (a, b) => a.priority - b.priority
      );
      partialInsights.brokerAccountInsights =
        partialInsights.brokerAccountInsights.slice(0, 4);
      this.priorityInsights = partialInsights;
    },
    brokerChanged(selected) {
      if (!selected?.length) {
        // If nothing selected, default to "All"
        this.accountSelected = [this.allAccountsOption];
        this.updateAnalytics(this.allAccountIds);
        return;
      }

      const newSelection = selected[selected.length - 1]; // Get the latest selection
      const wasAll = this.accountSelected.length === 1 && this.accountSelected[0].isAll;

      if (newSelection.isAll) {
        // User selected "All"
        this.accountSelected = [this.allAccountsOption];
        this.updateAnalytics(this.allAccountIds);
      } else if (wasAll) {
        // User selected specific account while "All" was selected
        this.accountSelected = [newSelection];
        this.updateAnalytics([Number(newSelection.value)]);
      } else {
        // Normal multi-select behavior
        this.accountSelected = selected.filter(item => !item.isAll);
        const selectedIds = this.accountSelected.map(item => Number(item.value));
        this.updateAnalytics(selectedIds);
      }
    },

    async updateAnalytics(selectedAccounts) {
      // Setup dates
      let nowFilters = new Date();
      nowFilters.setDate(nowFilters.getDate() + 1);
      let fromFilters = new Date();

      // Setting from time based on period
      if (this.periodSelected === "Current week") {
        fromFilters.setDate(
          fromFilters.getDate() - ((fromFilters.getDay() + 6) % 7)
        );
      } else if (this.periodSelected === "Current month") {
        fromFilters = new Date(
          fromFilters.getFullYear(),
          fromFilters.getMonth(),
          1
        );
      } else {
        let minusTime =
          this.periodSelected === "Last 7 days"
            ? 6.048e8
            : this.periodSelected === "Last 30 days"
              ? 2.628e9
              : 7.884e9;
        fromFilters.setTime(fromFilters.getTime() - minusTime);
      }

      // Create filter with selected accounts
      const filter = {
        allStrategies: true,
        userId: this.$store.state.user.id,
        fromDate: new Date(
          fromFilters.getFullYear(),
          fromFilters.getMonth(),
          fromFilters.getDate(),
          0,
          0
        ),
        toDate: new Date(
          nowFilters.getFullYear(),
          nowFilters.getMonth(),
          nowFilters.getDate(),
          0,
          0
        ),
        side: "All",
        moods: Moods.map((m) => m.key),
        assets: this.$store.state.user.assets.map((asset) => asset.ticker),
        brokerAccounts: selectedAccounts,
        result: "All",
        strategies: ["-1"],
      };

      // Update analytics
      await Repository.analyticsSummary(filter).then((response) => {
        if (response.status < 400) {
          this.analytics = response.data.content[0];
        }
      });

      // Update insights if needed
      this.setPriorityInsights();
    },
    async periodChanged() {
      // Dates
      let nowFilters = new Date();
      nowFilters.setDate(nowFilters.getDate() + 1);
      let fromFilters = new Date();

      // Setting from time
      if (this.periodSelected === "Current week")
        fromFilters.setDate(
          fromFilters.getDate() - ((fromFilters.getDay() + 6) % 7)
        );
      // set to Monday of this week
      else if (this.periodSelected === "Current month")
        fromFilters = new Date(
          fromFilters.getFullYear(),
          fromFilters.getMonth(),
          1
        );
      else {
        let minusTime =
          this.periodSelected === "Last 7 days"
            ? 6.048e8
            : this.periodSelected === "Last 30 days"
              ? 2.628e9
              : 7.884e9;
        fromFilters.setTime(fromFilters.getTime() - minusTime);
      }

      if (this.accountSelected == "All") {
        this.accountSelected = this.$store.state.user.brokerAccounts.map(
          (acc) => Number(acc.id)
        );
      } else {
        this.accountSelected = this.accountSelected.map((val) => Number(val));
      }

      let filter = {
        allStrategies: true,
        userId: this.$store.state.user.id,
        fromDate: new Date(
          fromFilters.getFullYear(),
          fromFilters.getMonth(),
          fromFilters.getDate(),
          0,
          0
        ),
        toDate: new Date(
          nowFilters.getFullYear(),
          nowFilters.getMonth(),
          nowFilters.getDate(),
          0,
          0
        ),
        side: "All",
        moods: Moods.map((m) => m.key),
        assets: this.$store.state.user.assets.map((asset) => asset.ticker),
        brokerAccounts: this.accountSelected,
        result: "All",
        strategies: ["-1"],
      };

      await Repository.analyticsSummary(filter).then((response) => {
        if (response.status < 400) this.analytics = response.data.content[0];
      });
    },
    getNumber(number) {
      return number > 1000 || number < -1000
        ? `${(number / 1000).toFixed(2)}k`
        : (number.toFixed(2) * 1).toString();
    },
    getRatioName(ratio) {
      if (this.analytics != null) {
        switch (ratio) {
          case "Profit Factor":
            return this.$t("ratios.profitFactor");
          case "Winning Ratio":
            return this.$t("ratios.winning");
          case "Swiset Ratio":
            return this.$t("ratios.swiset");
          case "Risk/Reward Ratio":
            return this.$t("ratios.riskReward");
          case "Net Profit":
            return this.$t("ratios.netProfit");
        }
      }
    },
    getRatio(ratio) {
      if (this.analytics != null) {
        switch (ratio) {
          case "Profit Factor":
            return this.analytics.profitFactor.toFixed(2);
          case "Winning Ratio":
            return `${(this.analytics.winRate * 100).toFixed(1)}%`;
          case "Swiset Ratio":
            return this.analytics.swisetRatio.toFixed(2);
          case "Risk/Reward Ratio":
            return `1 : ${this.analytics.riskReward.toFixed(2)}`;
          case "Net Profit":
            return this.getNumber(this.analytics.netProfit);
        }
      }
    },
    getRatioColor(ratio) {
      if (this.analytics != null) {
        switch (ratio) {
          case "Profit Factor":
            return this.analytics.profitFactor > 1 ? "var(--v-darkGreen-base)" : "#f14400";
          case "Winning Ratio":
            return "#111111";
          case "Swiset Ratio":
            return this.analytics.swisetRatio > 1 ? "var(--v-darkGreen-base)" : "#f14400";
          case "Risk/Reward Ratio":
            return "#111111";
          case "Net Profit":
            return this.analytics.netProfit > 0 ? "var(--v-darkGreen-base)" : "#f14400";
        }
      }
    },
    getRatioBorder(ratio) {
      if (this.analytics != null) {
        switch (ratio) {
          case "Profit Factor":
            return this.analytics.profitFactor > 1
              ? "positive-border-box"
              : "negative-border-box";
          case "Winning Ratio":
            return "neutral-border-box";
          case "Swiset Ratio":
            return this.analytics.swisetRatio > 1
              ? "positive-border-box"
              : "negative-border-box";
          case "Risk/Reward Ratio":
            return "neutral-border-box";
          case "Net Profit":
            return this.analytics.netProfit > 0
              ? "positive-border-box"
              : "negative-border-box";
        }
      }
    },
    closeOverlayUpgradeToPro() {
      this.overlayUpgradeToPro = false;
    },
  },
};
</script>
<style scoped lang="scss">
.positive-border-box {
  border-top: 5px solid var(--v-darkGreen-base) !important;
}

.negative-border-box {
  border-top: 5px solid #f14400 !important;
}

.neutral-border-box {
  border-top: 5px solid #4e4e4e !important;
}

.button-convert {
  background-color: #fff75f !important;
  color: #111111 !important;
  border: #fff75f solid 1px;
}

.v-select.v-text-field--outlined {
  .v-chip {
    margin: 2px 4px;
    
    &__close {
      opacity: 0.7;
      
      &:hover {
        opacity: 1;
      }
    }
  }
}

.broker-select {
  max-width: 300px; // Adjust this value as needed
  
  ::v-deep .v-select__selection {
    max-width: 100%;
    display: flex !important;
    align-items: center;
    
    .account-selection-text {
      max-width: 200px; // Reduced to account for the counter
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
      line-height: normal;
      vertical-align: middle;
      margin-top: 5px;
      font-size: 14px;
    }
  }

  ::v-deep .v-select__selections {
    flex-wrap: nowrap !important;
    overflow: hidden;
    font-size: 14px;
    margin-top: 5px;
  }
}

</style>
